import { useCallback } from 'react';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { EventData } from '../../components/calendar-view/types';
import { REQUEST_EVENT_BACKGROUND_COLOR } from '../../constants';
import { getDateString, getTimeString } from '../../utils/date-helpers';

type GetScheduleRequestByProviderIdParamsType = {
  scheduleRequests: ScheduleRequestEvent[] | undefined;
  providerId: string;
  locationId: string;
  providerName: string;
};

type GetScheduleRequestByOperatoryIdParamsType = {
  scheduleRequests: ScheduleRequestEvent[] | undefined;
  operatoryId?: string | null;
  locationId: string;
};

type GetCalendarEventByProviderIdParamsType = {
  calendarEvents: CalendarEvent[];
  providerId: string;
  locationId: string;
};

type GetCalendarEventByOperatoryIdParamsType = {
  calendarEvents: CalendarEvent[];
  operatoryId?: string | null;
  locationId: string;
};

export const useGetCalendarEventsV3 = () => {
  const getMappedCalendarEventData = (calendarEvent: CalendarEvent): EventData => {
    const hasName = calendarEvent.attendeeDetails?.firstName || calendarEvent.attendeeDetails?.lastName;
    const personName = `${calendarEvent.attendeeDetails?.firstName} ${calendarEvent?.attendeeDetails?.lastName}`.trim();
    const name = hasName ? personName : 'Unknown';

    const startDate = dayjs(`${calendarEvent.startDate} ${calendarEvent.startTime}`);
    const endDate = dayjs(`${calendarEvent.endDate} ${calendarEvent.endTime}`);

    return {
      id: calendarEvent.id ?? '',
      isNonIntegratedSource: !calendarEvent.isIntegrated,
      startHour: getTimeString(startDate),
      endHour: getTimeString(endDate),
      type: calendarEvent.details?.rTyNa ?? '',
      status: calendarEvent.attendeeStatus,
      appointmentType: calendarEvent.eventType,
      appointmentTypeId: calendarEvent.referenceTypeId ?? '',
      eventId: calendarEvent.id ?? '',
      createdBySourceId: calendarEvent.sourceTenantId ?? '',
      workstationIds: calendarEvent.operatoryId ? [calendarEvent.operatoryId] : [],
      name: name,
      patientId: calendarEvent.attendeeId ?? '',
      locationId: calendarEvent.locationId ?? '',
      startDate: getDateString(startDate),
      endDate: getDateString(endDate),
      referenceId: calendarEvent.referenceId ?? '',
      personStatus: calendarEvent.attendeeDetails?.status ?? '',
      displayOptions: {
        primaryColor: calendarEvent.displayOptions?.primaryColor,
        backgroundColor: calendarEvent.displayOptions?.backgroundColor ?? undefined,
        textColor: calendarEvent.displayOptions?.textColor ?? undefined,
      },
    };
  };

  const getMappedScheduleRequestData = (scheduleRequest: ScheduleRequestEvent): EventData => {
    const startDate = dayjs(`${scheduleRequest.startDate} ${scheduleRequest.startTime}`);
    const endDate = dayjs(`${scheduleRequest.endDate} ${scheduleRequest.endTime}`);

    return {
      name: scheduleRequest.request.appointmentType || '',
      eventId: scheduleRequest.request.id || '',
      type: scheduleRequest.type || '',
      eventType: 'request',
      appointmentType: scheduleRequest.request.appointmentType,
      appointmentTypeId: scheduleRequest.referenceTypeId,
      locationId: scheduleRequest.request.locationId || '',
      startHour: getTimeString(startDate),
      endHour: getTimeString(endDate),
      startDate: getDateString(startDate),
      endDate: getDateString(endDate),
      backgroundColor: REQUEST_EVENT_BACKGROUND_COLOR,
      displayOptions: {
        primaryColor: scheduleRequest.displayOptions.primaryColor,
        backgroundColor: scheduleRequest.displayOptions.backgroundColor,
      },
      request: scheduleRequest.request,
    };
  };

  const getProviderCalendarEvents = useCallback(
    ({ calendarEvents, locationId, providerId }: GetCalendarEventByProviderIdParamsType): EventData[] => {
      return (calendarEvents ?? [])
        .filter(
          (event) =>
            event.organizerId !== undefined && event.locationId === locationId && event.organizerId === providerId //  event.organizerId is '' for unassigned providers
        )
        .map((calendarEvent) => getMappedCalendarEventData(calendarEvent));
    },
    []
  );

  const getOperatoryCalendarEvents = useCallback(
    ({ calendarEvents, locationId, operatoryId }: GetCalendarEventByOperatoryIdParamsType): EventData[] => {
      return (calendarEvents ?? [])
        .filter(
          (event) =>
            event.operatoryId !== undefined && event.locationId === locationId && event.operatoryId === operatoryId //  event.operatoryId is '' for unassigned operatories
        )
        .map((calendarEvent) => getMappedCalendarEventData(calendarEvent));
    },
    []
  );

  const getProviderScheduleRequestEvents = useCallback(
    ({
      scheduleRequests,
      locationId,
      providerId,
      providerName,
    }: GetScheduleRequestByProviderIdParamsType): EventData[] => {
      return (scheduleRequests ?? [])
        .filter((sr) => {
          return sr.locationId === locationId && (providerId === sr.organizerId || providerName === sr.providerName);
        })
        .map((request) => getMappedScheduleRequestData(request));
    },
    []
  );

  const getOperatoryScheduleRequestEvents = useCallback(
    ({ scheduleRequests, locationId, operatoryId }: GetScheduleRequestByOperatoryIdParamsType): EventData[] => {
      return (scheduleRequests ?? [])
        .filter((sr) => {
          return sr.locationId === locationId && operatoryId === sr.operatoryId;
        })
        .map((request) => getMappedScheduleRequestData(request));
    },
    []
  );

  return {
    getProviderCalendarEvents,
    getOperatoryCalendarEvents,
    getProviderScheduleRequestEvents,
    getOperatoryScheduleRequestEvents,
  };
};
